import * as React from "react";
import { Copy, Loader2 } from "lucide-react";

import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from "@/components/ui/toast-legacy";

function flattenToastContent(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenToastContent).filter(Boolean).join(" ");
  if (React.isValidElement(node)) return flattenToastContent(node.props.children);
  return "";
}

function ToastCopyButton({ title, description }: { title?: React.ReactNode; description?: React.ReactNode }) {
  const [isCopying, setIsCopying] = React.useState(false);

  const handleCopy = async () => {
    const text = [flattenToastContent(title), flattenToastContent(description)].filter(Boolean).join("\n");
    if (!text) return;

    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(text);
    } finally {
      setIsCopying(false);
    }
  };

  if (!title && !description) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleCopy}
      disabled={isCopying}
      className="absolute bottom-1.5 right-1.5 h-5 w-5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 group-[.destructive]:text-destructive-foreground/85 text-muted-foreground hover:text-foreground group-[.destructive]:hover:text-destructive-foreground"
      aria-label="Copy toast details"
    >
      {isCopying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}

/**
 * Pre-ENT-1956 toast rendering, retained as the fallback when the `modern_toast`
 * feature flag is OFF (kill-switch / revert path). Do not add new features here —
 * this is the legacy look, scheduled for removal once the flag is retired.
 */
export function LegacyToaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid flex-1 gap-0.5 pb-5 pr-6">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
            </div>
            {action}
            <ToastCopyButton title={title} description={description} />
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
